import { type ThemeType } from "@cruk/cruk-react-components";
import styled from "styled-components";
import {
  Heading,
  Box,
  ProgressBar,
  Text,
  crukTheme,
  IconFa,
} from "@cruk/cruk-react-components";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";

import {
  calculatePercentRounded,
  numberWithCommas,
} from "@fwa/src/utils/formatUtils";

import {
  distanceMetersToUnit,
  targetMetersToUnit,
} from "@fwa/src/utils/distanceUtils";
import { type StravaType } from "@fwa/src/types";

const stravaLogo = "/assets/images/logos/strava-rounded-128.png";

const MainWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const RowAligned = styled.div<{ theme: ThemeType }>`
  display: flex;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing.s};
`;

const TextWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  position: relative;
`;

const StyledLogo = styled.img<{ theme: ThemeType }>`
  border-radius: 50%;
  height: ${({ theme }) => theme.spacing.m};
  width: ${({ theme }) => theme.spacing.m};
  margin-right: ${({ theme }) => theme.spacing.xxs};
  vertical-align: bottom;
`;

const FloatingIcon = styled.div`
  position: absolute;
  right: 0;
`;

export const StravaTracker = ({
  canEdit,
  strava,
}: {
  canEdit?: boolean;
  strava: StravaType;
}) => {
  const { useMetric, totalDistance, targetDistance } = strava;
  const unit = useMetric ? "km" : "miles";

  return (
    <Box margin="none" paddingRight="xs">
      <RowAligned>
        <StyledLogo src={stravaLogo} alt="" />
        <Heading h2 marginVertical="none" textSize="xl">
          Strava activity tracker
        </Heading>
      </RowAligned>
      <MainWrapper>
        <ProgressBar
          circleSize="110px"
          barColor={crukTheme.tokenColors.cyan_900}
          circleContents={
            <Box>
              <Text
                textAlign="center"
                textSize="l"
                marginBottom="none"
                marginHorizontal="xs"
              >
                {numberWithCommas(
                  distanceMetersToUnit(totalDistance, useMetric),
                )}
              </Text>
              <Text textAlign="center">{unit}</Text>
            </Box>
          }
          percentage={
            targetDistance
              ? calculatePercentRounded(
                  distanceMetersToUnit(totalDistance, useMetric),
                  targetMetersToUnit(targetDistance, useMetric),
                )
              : 0
          }
          isCircular
        />
        {targetDistance ? (
          <TextWrapper>
            <Text
              textAlign="center"
              textSize="xl"
              marginLeft="xs"
              marginBottom="none"
            >
              {`${calculatePercentRounded(
                distanceMetersToUnit(totalDistance, useMetric),
                targetMetersToUnit(targetDistance, useMetric),
              )}%`}
            </Text>
            <Text
              marginLeft="xs"
              marginRight="m"
              marginBottom="none"
            >{` of ${numberWithCommas(
              targetMetersToUnit(targetDistance, useMetric),
            )} ${unit} target`}</Text>
            {canEdit && (
              <FloatingIcon>
                <IconFa faIcon={faPenToSquare} />
              </FloatingIcon>
            )}
          </TextWrapper>
        ) : (
          <Text marginHorizontal="xs">
            {`Distance travelled ${numberWithCommas(
              distanceMetersToUnit(totalDistance, useMetric),
            )} ${unit}`}
          </Text>
        )}
      </MainWrapper>
    </Box>
  );
};

export default StravaTracker;
